import FilterContainer, { FilterProps } from '../filter-container';
import React, { useEffect, useState } from 'react';
import { FlexBox, FlexColumn, RadioButton } from '@make-software/cspr-design';
import styled from 'styled-components';
import { BodyText, Tooltip } from '@block-explorer/ui';
import { useFilterQuery } from '../filter-query';
import { truncateName } from '../../action-messages/utils';

const InputContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'row'],
    borderBottom: theme.border.separator,
    paddingBottom: 12,
    alignItems: 'flex-start',
    maxHeight: 208,
    overflowY: 'auto',
  })
);

type Option = { value: string; label: string };

interface OptionFilterProps extends FilterProps {
  filterId: string;
  label?: string;
  options: Option[];
}

const OPTION_ITEM_LENGTH_LIMIT = 30;

export const RadioFilter = ({
  filterId,
  filters,
  setFilters,
  options,
  label,
}: OptionFilterProps) => {
  const { queryFilters, syncFiltersToQuery } = useFilterQuery();
  const queryFilterValueRaw = queryFilters?.[filterId];
  const queryFilterValue = Array.isArray(queryFilterValueRaw)
    ? queryFilterValueRaw[0]
    : queryFilterValueRaw;
  const initialInputValue = queryFilterValue || filters?.[filterId] || '';
  const [inputValue, setInputValue] = useState<string>(
    String(initialInputValue)
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (queryFilterValue == null) return;

    const nextInputValue = String(queryFilterValue);
    if (inputValue !== nextInputValue) {
      setInputValue(nextInputValue);
      setError(null);
    }

    if (filters?.[filterId] !== nextInputValue) {
      setFilters({
        ...(filters || {}),
        [filterId]: nextInputValue,
      });
    }
  }, [queryFilterValue, filterId]);

  const handleSetFilters = () => {
    const newFilters = { ...(filters || {}), [filterId]: inputValue };
    setFilters(newFilters);
    syncFiltersToQuery(newFilters);
  };

  const handleResetFilters = () => {
    setInputValue('');
    setError(null);

    const { [filterId]: _, ...rest } = filters || {};
    setFilters(rest);
    syncFiltersToQuery(rest);
  };

  const handleChooseFilterOption = (value) => {
    setInputValue(value);
  };

  const isFilterSet =
    Object.keys(filters || {})?.includes(filterId) &&
    !!filters?.[filterId]?.length;
  const disabled = !inputValue || !!error;

  const truncatedLabel = (label: string) => {
    const hasTooltip = label.length > OPTION_ITEM_LENGTH_LIMIT;
    return hasTooltip ? (
      <Tooltip tooltipContent={label}>
        <BodyText size={3} wordBreak noWrap>
          {truncateName(label, OPTION_ITEM_LENGTH_LIMIT)}
        </BodyText>
      </Tooltip>
    ) : (
      <BodyText size={3} wordBreak noWrap>
        {label}
      </BodyText>
    );
  };

  return (
    <FilterContainer
      isFilterSet={isFilterSet}
      onSetFilters={handleSetFilters}
      onResetFilters={handleResetFilters}
      onResetErrors={() => setError(null)}
      disabled={disabled}
    >
      <FlexColumn itemsSpacing={12}>
        {label && (
          <BodyText variation={'black'} size={1}>
            {label}
          </BodyText>
        )}
        <InputContainer>
          <FlexColumn itemsSpacing={12}>
            {options.map(({ value, label }) => (
              <BodyText
                variation={'black'}
                key={'option-rb-' + label.toLowerCase()}
                size={3}
              >
                <RadioButton
                  size={20}
                  onChange={() => handleChooseFilterOption(value)}
                  value={value}
                  selected={inputValue}
                  label={truncatedLabel(label)}
                />
              </BodyText>
            ))}
          </FlexColumn>
        </InputContainer>
      </FlexColumn>
    </FilterContainer>
  );
};
